(() => {
  const ROOT_SELECTOR = '[data-vitea-wheel]';

  function init(root) {
    if (!root || root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';

    const editorOnly = root.dataset.editorOnly === 'true';
    const designMode = Boolean(window.Shopify && Shopify.designMode);
    if (editorOnly && !designMode) {
      root.hidden = true;
      return;
    }
    root.hidden = false;

    const overlay = root.querySelector('[data-wheel-overlay]');
    const teaserWrap = root.querySelector('[data-wheel-teaser-wrap]');
    const teaser = root.querySelector('[data-wheel-teaser]');
    const teaserDismiss = root.querySelector('[data-wheel-teaser-dismiss]');
    const closeButtons = root.querySelectorAll('[data-wheel-close]');
    const form = root.querySelector('[data-wheel-form]');
    const email = root.querySelector('[data-wheel-email]');
    const marketingConsent = root.querySelector('[data-wheel-marketing-consent]');
    const spinButton = root.querySelector('[data-wheel-spin]');
    const disc = root.querySelector('[data-wheel-disc]');
    const errorEl = root.querySelector('[data-wheel-error]');
    const resultCard = root.querySelector('[data-wheel-result]');
    const miniResult = root.querySelector('[data-wheel-mini-result]');
    const copyButton = root.querySelector('[data-wheel-copy]');
    const minimizeButton = root.querySelector('[data-wheel-minimize]');
    const resultTitle = root.querySelector('[data-wheel-result-title]');
    const resultCode = root.querySelector('[data-wheel-code]');
    const timerValue = root.querySelector('[data-wheel-timer]');
    const miniTitle = root.querySelector('[data-wheel-mini-title]');
    const miniTimer = root.querySelector('[data-wheel-mini-timer]');
    const editorReset = root.querySelector('[data-wheel-editor-reset]');
    const entryPanel = root.querySelector('[data-wheel-entry]');
    const celebration = root.querySelector('[data-wheel-celebration]');
    const celebrationPrize = root.querySelector('[data-wheel-celebration-prize]');
    const celebrationKicker = root.querySelector('[data-wheel-celebration-kicker]');
    const celebrationSub = root.querySelector('[data-wheel-celebration-sub]');
    const confettiLayer = root.querySelector('[data-wheel-confetti]');

    if (!overlay || !form || !disc || !teaserWrap || !teaser) return;

    const sectionId = root.dataset.sectionId || 'default';
    const storagePrefix = `vitea-wheel-${window.location.host}-${sectionId}`;
    const resultKey = `${storagePrefix}-result`;
    const usedEmailsKey = `${storagePrefix}-used-emails`;
    const tabPositionKey = `${storagePrefix}-tab-position-v1`;
    const wheelDiscountKey = `${storagePrefix}-cart-discount-v1`;
    const teaserReopenedKey = `${storagePrefix}-teaser-reopened-v28`;
    const teaserCollapsedKey = `${storagePrefix}-teaser-collapsed-v28`;
    const autoPromptSeenKey = `${storagePrefix}-auto-prompt-seen`;
    const autoPromptStartedKey = `${storagePrefix}-auto-prompt-started`;
    const discountHours = clampNumber(root.dataset.discountHours, 1, 168, 24);
    const autoOpenEnabled = root.dataset.autoOpenEnabled !== 'false';
    const autoOpenSeconds = clampNumber(root.dataset.autoOpenSeconds, 3, 60, 7);
    const celebrationEnabled = root.dataset.celebrationEnabled !== 'false';
    const confettiEnabled = root.dataset.confettiEnabled !== 'false';
    const celebrationSeconds = clampNumber(root.dataset.celebrationSeconds, 2, 5, 3);
    const testMode = root.dataset.testMode === 'true';
    const backendEnabled = root.dataset.backendEnabled === 'true';
    const backendUrl = String(root.dataset.backendUrl || '').trim().replace(/\/$/, '');
    const forcedPrizeId = root.dataset.forcePrize || 'random';
    const wheelLanguage = getLanguage(root.dataset.languageMode);
    const i18nNode = root.querySelector('[data-wheel-i18n]');
    const copyLabels = i18nNode ? JSON.parse(i18nNode.textContent || '{}') : {};
    const text = copyLabels[wheelLanguage] || copyLabels.nl || {};
    const prizes = readPrizes(root);
    let rotation = 0;
    let timerId = null;
    let lastFocused = null;
    let restoreResultOnClose = false;
    let previewTeaserReopened = false;
    let previewTeaserCollapsed = false;
    const tabMinPercent = 25;
    const tabMaxPercent = 75;
    const defaultTabPosition = clampNumber(root.dataset.tabPosition, tabMinPercent, tabMaxPercent, 55);
    let previewTabPosition = defaultTabPosition;


    function getLanguage(mode) {
      if (mode !== 'automatic') return mode || 'nl';
      const locale = ((window.Shopify && Shopify.locale) || document.documentElement.lang || 'nl').toLowerCase();
      if (locale.startsWith('es')) return 'es';
      if (locale.startsWith('en')) return 'en';
      return 'nl';
    }

    function readPrizes(container) {
      return [...container.querySelectorAll('[data-wheel-prize]')].map((node, index) => ({
        index,
        id: node.dataset.prizeId,
        weight: Math.max(0, parseFloat(node.dataset.weight) || 0),
        title: node.dataset.title || '',
        codePrefix: node.dataset.codePrefix || 'VITEA',
        jackpot: node.dataset.jackpot === 'true'
      }));
    }

    function clampNumber(value, min, max, fallback) {
      const parsed = Number(value);
      if (!Number.isFinite(parsed)) return fallback;
      return Math.min(max, Math.max(min, parsed));
    }

    function loadTabPosition() {
      if (designMode) return previewTabPosition;
      try {
        const raw = localStorage.getItem(tabPositionKey);
        if (raw === null || raw === '') return defaultTabPosition;
        const stored = Number(raw);
        return Number.isFinite(stored)
          ? clampNumber(stored, tabMinPercent, tabMaxPercent, defaultTabPosition)
          : defaultTabPosition;
      } catch (e) {
        return defaultTabPosition;
      }
    }

    function applyTabPosition(value) {
      const position = clampNumber(value, tabMinPercent, tabMaxPercent, defaultTabPosition);
      root.style.setProperty('--vw-tab-top', `${position}%`);
      return position;
    }

    function saveTabPosition(value) {
      const position = applyTabPosition(value);
      if (designMode) {
        previewTabPosition = position;
        return position;
      }
      try { localStorage.setItem(tabPositionKey, String(position)); } catch (e) {}
      return position;
    }

    function makeVerticallyDraggable(element) {
      if (!element || typeof window.PointerEvent === 'undefined') return;
      let drag = null;

      element.addEventListener('pointerdown', event => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        drag = {
          pointerId: event.pointerId,
          startY: event.clientY,
          startPosition: loadTabPosition(),
          moved: false
        };
        try { element.setPointerCapture(event.pointerId); } catch (e) {}
      });

      element.addEventListener('pointermove', event => {
        if (!drag || event.pointerId !== drag.pointerId) return;
        const deltaY = event.clientY - drag.startY;
        if (!drag.moved && Math.abs(deltaY) < 6) return;
        drag.moved = true;
        element.classList.add('is-dragging');
        const viewportHeight = Math.max(window.innerHeight || document.documentElement.clientHeight || 1, 1);
        applyTabPosition(drag.startPosition + ((deltaY / viewportHeight) * 100));
        if (event.cancelable) event.preventDefault();
      });

      const finishDrag = event => {
        if (!drag || event.pointerId !== drag.pointerId) return;
        const moved = drag.moved;
        drag = null;
        element.classList.remove('is-dragging');
        try { element.releasePointerCapture(event.pointerId); } catch (e) {}
        if (!moved) return;

        const current = parseFloat(root.style.getPropertyValue('--vw-tab-top'));
        saveTabPosition(current);

        // A pointerup on a button normally also emits a click. Suppress only
        // that one click after a real drag so dragging never opens the wheel.
        element.dataset.wheelDragged = 'true';
        window.setTimeout(() => { delete element.dataset.wheelDragged; }, 0);
      };

      element.addEventListener('pointerup', finishDrag);
      element.addEventListener('pointercancel', finishDrag);
      element.addEventListener('click', event => {
        if (element.dataset.wheelDragged !== 'true') return;
        event.preventDefault();
        event.stopImmediatePropagation();
        delete element.dataset.wheelDragged;
      }, true);
    }

    function shopifyCartUrl(path) {
      const shopRoot = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      return `${shopRoot}${path}`;
    }

    function shopifyDiscountUrl(code) {
      return `/discount/${encodeURIComponent(String(code || '').trim())}`;
    }

    function extractCartDiscountCodes(cart) {
      if (!cart || !Array.isArray(cart.discount_codes)) return [];
      const unique = [];
      cart.discount_codes.forEach(item => {
        const code = typeof item === 'string' ? item : (item && item.code);
        const normalized = String(code || '').trim();
        if (normalized && !unique.some(existing => existing.toLowerCase() === normalized.toLowerCase())) {
          unique.push(normalized);
        }
      });
      return unique;
    }

    async function fetchCartState() {
      const response = await fetch(shopifyCartUrl('cart.js'), {
        method: 'GET',
        credentials: 'same-origin',
        headers: { 'Accept': 'application/json' }
      });
      if (!response.ok) throw new Error(`CART_READ_${response.status}`);
      return response.json();
    }

    async function updateCartDiscountCodes(codes) {
      const response = await fetch(shopifyCartUrl('cart/update.js'), {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ discount: codes.join(',') })
      });
      if (!response.ok) throw new Error(`CART_UPDATE_${response.status}`);
      return response.json();
    }

    function loadStoredWheelDiscount() {
      try {
        const parsed = JSON.parse(localStorage.getItem(wheelDiscountKey) || 'null');
        if (!parsed || !parsed.code || !Number.isFinite(Number(parsed.expiresAt))) return null;
        return {
          code: String(parsed.code),
          expiresAt: Number(parsed.expiresAt),
          applied: parsed.applied === true
        };
      } catch (e) {
        return null;
      }
    }

    function saveStoredWheelDiscount(record) {
      if (designMode || !record || !record.code || !Number.isFinite(Number(record.expiresAt))) return;
      try {
        localStorage.setItem(wheelDiscountKey, JSON.stringify({
          code: String(record.code),
          expiresAt: Number(record.expiresAt),
          applied: record.applied === true
        }));
      } catch (e) {}
    }

    async function applyWheelDiscount(result) {
      if (designMode || !result || result.jackpot || !result.code) return;
      const expiresAt = Number(result.expiresAt);
      if (!Number.isFinite(expiresAt) || expiresAt <= Date.now()) return;

      const existingRecord = loadStoredWheelDiscount();
      const sameCode = existingRecord && existingRecord.code.toLowerCase() === String(result.code).toLowerCase();
      const record = {
        code: String(result.code),
        expiresAt,
        applied: Boolean(sameCode && existingRecord.applied)
      };
      saveStoredWheelDiscount(record);
      if (record.applied) return;

      try {
        const response = await fetch(shopifyDiscountUrl(record.code), {
          method: 'GET',
          credentials: 'same-origin',
          redirect: 'follow',
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        });
        if (!response.ok) throw new Error(`DISCOUNT_APPLY_${response.status}`);
        record.applied = true;
        saveStoredWheelDiscount(record);
      } catch (error) {
        // The prize itself remains valid even if the cart request is temporarily
        // unavailable. Keeping applied=false lets the next page load retry once.
        console.warn('[Vitea Prize Wheel] Kon de gewonnen code niet automatisch op de winkelwagen toepassen:', error);
      }
    }

    async function removeExpiredWheelDiscount(record) {
      if (designMode || !record || !record.code) return;
      let cleanupComplete = false;
      try {
        const cart = await fetchCartState();
        const currentCodes = extractCartDiscountCodes(cart);
        const containsWheelCode = currentCodes.some(code => code.toLowerCase() === record.code.toLowerCase());

        // Only alter the cart when Shopify explicitly reports this exact wheel
        // code. This prevents us from accidentally clearing a different code.
        if (containsWheelCode) {
          const remainingCodes = currentCodes.filter(code => code.toLowerCase() !== record.code.toLowerCase());
          await updateCartDiscountCodes(remainingCodes);
        }
        cleanupComplete = true;
      } catch (error) {
        // Keep the cleanup record when the network is unavailable so the next
        // page load can retry. Shopify itself still enforces the expiry time.
        console.warn('[Vitea Prize Wheel] Kon de verlopen radcode niet uit de winkelwagen opruimen:', error);
      }

      if (cleanupComplete) {
        try { localStorage.removeItem(wheelDiscountKey); } catch (e) {}
      }
    }

    function reconcileWheelDiscount(result) {
      if (designMode) return;
      const stored = loadStoredWheelDiscount();
      if (stored && stored.expiresAt <= Date.now()) {
        void removeExpiredWheelDiscount(stored);
        return;
      }

      if (result && !result.jackpot && result.code && Number(result.expiresAt) > Date.now()) {
        const sameStoredCode = stored && stored.code.toLowerCase() === String(result.code).toLowerCase();
        if (!sameStoredCode) {
          saveStoredWheelDiscount({ code: result.code, expiresAt: result.expiresAt, applied: false });
        }
        void applyWheelDiscount(result);
      }
    }

    function normalizeEmail(value) {
      return String(value || '').trim().toLowerCase();
    }

    function loadUsedEmails() {
      try {
        const parsed = JSON.parse(localStorage.getItem(usedEmailsKey) || '[]');
        return Array.isArray(parsed) ? parsed.map(normalizeEmail).filter(Boolean) : [];
      } catch (e) {
        return [];
      }
    }

    function isEmailUsed(value) {
      if (designMode) return false;
      return loadUsedEmails().includes(normalizeEmail(value));
    }

    function markEmailUsed(value) {
      if (designMode) return;
      const normalized = normalizeEmail(value);
      if (!normalized) return;
      try {
        const emails = loadUsedEmails();
        if (!emails.includes(normalized)) emails.push(normalized);
        localStorage.setItem(usedEmailsKey, JSON.stringify(emails.slice(-250)));
      } catch (e) {}
    }

    function teaserWasReopened() {
      if (designMode) return previewTeaserReopened;
      try { return sessionStorage.getItem(teaserReopenedKey) === '1'; } catch (e) { return false; }
    }

    function markTeaserReopened() {
      if (designMode) {
        previewTeaserReopened = true;
        return;
      }
      try { sessionStorage.setItem(teaserReopenedKey, '1'); } catch (e) {}
    }

    function teaserIsCollapsed() {
      if (designMode) return previewTeaserCollapsed;
      try { return sessionStorage.getItem(teaserCollapsedKey) === '1'; } catch (e) { return false; }
    }

    function setTeaserCollapsed(collapsed) {
      if (designMode) {
        previewTeaserCollapsed = Boolean(collapsed);
        return;
      }
      try {
        if (collapsed) sessionStorage.setItem(teaserCollapsedKey, '1');
        else sessionStorage.removeItem(teaserCollapsedKey);
      } catch (e) {}
    }

    function autoPromptWasSeen() {
      if (designMode) return false;
      try { return sessionStorage.getItem(autoPromptSeenKey) === '1'; } catch (e) { return false; }
    }

    function markAutoPromptSeen() {
      if (designMode) return;
      try { sessionStorage.setItem(autoPromptSeenKey, '1'); } catch (e) {}
    }

    function getAutoPromptStartedAt() {
      if (designMode) return Date.now();
      try {
        const stored = Number(sessionStorage.getItem(autoPromptStartedKey));
        if (Number.isFinite(stored) && stored > 0) return stored;
        const now = Date.now();
        sessionStorage.setItem(autoPromptStartedKey, String(now));
        return now;
      } catch (e) {
        return Date.now();
      }
    }

    function isVisibleElement(node) {
      if (!node) return false;
      const style = window.getComputedStyle(node);
      const rect = node.getBoundingClientRect();
      return style.display !== 'none' && style.visibility !== 'hidden' && Number(style.opacity || 1) !== 0 && rect.width > 0 && rect.height > 0;
    }

    function cookieBannerIsVisible() {
      const selectors = [
        '#shopify-pc__banner',
        '.shopify-pc__banner',
        '[data-shopify-privacy-banner]',
        '[class*="shopify-pc__banner"]'
      ];
      return selectors.some(selector => {
        try { return [...document.querySelectorAll(selector)].some(isVisibleElement); } catch (e) { return false; }
      });
    }

    function showTeaser() {
      if (loadResult()) {
        hideTeaser();
        return;
      }

      const collapsed = teaserIsCollapsed();
      teaserWrap.hidden = false;
      teaserWrap.classList.toggle('is-collapsed', collapsed);

      // Het kruisje verschijnt pas nadat iemand de kleine teaser bewust
      // heeft geopend en het rad daarna weer heeft gesloten zonder te draaien.
      if (teaserDismiss) {
        teaserDismiss.hidden = collapsed || !teaserWasReopened();
      }
    }

    function hideTeaser() {
      teaserWrap.hidden = true;
    }

    function scheduleAutoOpen() {
      hideTeaser();

      if (!autoOpenEnabled || autoPromptWasSeen()) {
        showTeaser();
        return;
      }

      const startedAt = getAutoPromptStartedAt();
      const elapsed = Math.max(0, Date.now() - startedAt);
      const delay = Math.max(0, (autoOpenSeconds * 1000) - elapsed);

      window.setTimeout(function attemptAutoOpen() {
        if (loadResult()) return;
        if (overlay.classList.contains('is-open')) return;

        // Laat Shopify's cookietoestemming altijd voorgaan. Zodra die weg is,
        // verschijnt het rad rustig daarna.
        if (cookieBannerIsVisible()) {
          window.setTimeout(attemptAutoOpen, 600);
          return;
        }

        markAutoPromptSeen();
        openModal();
      }, delay);
    }

    function openModal(options = {}) {
      restoreResultOnClose = Boolean(options.restoreResult);
      lastFocused = document.activeElement;
      const scrollX = window.scrollX || window.pageXOffset || 0;
      const scrollY = window.scrollY || window.pageYOffset || 0;
      if (options.clearEmail && email) email.value = '';
      errorEl.textContent = '';
      if (entryPanel) entryPanel.hidden = false;
      if (celebration) { celebration.hidden = true; celebration.classList.remove('is-visible', 'is-jackpot'); }
      clearConfetti();
      overlay.classList.add('is-open');
      overlay.setAttribute('aria-hidden', 'false');
      document.body.classList.add('vitea-wheel-lock');

      // Do not autofocus the email field. Mobile browsers can otherwise scroll to
      // the section's physical DOM position before rendering the fixed dialog.
      // Keep the shopper exactly where they were when opening the wheel.
      requestAnimationFrame(() => {
        if (Math.abs((window.scrollX || 0) - scrollX) > 1 || Math.abs((window.scrollY || 0) - scrollY) > 1) {
          window.scrollTo(scrollX, scrollY);
        }
        const closeButton = overlay.querySelector('[data-wheel-close]');
        if (closeButton && typeof closeButton.focus === 'function') {
          try { closeButton.focus({ preventScroll: true }); } catch (e) {}
        }
      });
    }

    function closeModal(options = {}) {
      overlay.classList.remove('is-open');
      overlay.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('vitea-wheel-lock');
      const shouldRestore = restoreResultOnClose && !options.skipRestore;
      restoreResultOnClose = false;
      if (shouldRestore) {
        const existing = loadResult();
        if (existing) showResult(existing, false);
      }
      if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
    }

    function closeModalAndRevealTab() {
      closeModal();

      // Als er nog niet gedraaid is, komt de teaser ALTIJD terug —
      // zowel live als in de Theme Editor. Heeft iemand de teaser eerder
      // geopend, dan toont showTeaser() hem vanaf nu met het kruisje.
      if (!loadResult()) {
        markAutoPromptSeen();
        showTeaser();
      }
    }

    function weightedPick() {
      const total = prizes.reduce((sum, prize) => sum + prize.weight, 0);
      if (total <= 0) return prizes[0];
      let point = Math.random() * total;
      for (const prize of prizes) {
        point -= prize.weight;
        if (point < 0) return prize;
      }
      return prizes[prizes.length - 1];
    }


    function prizeFromBackend(payload) {
      const keyMap = {
        discount_5: 'p5',
        discount_10: 'p10',
        discount_15: 'p15',
        discount_20: 'p20',
        shop_credit_250: 'p250'
      };
      const backendKey = payload && payload.prize ? payload.prize.key : '';
      const prizeId = keyMap[backendKey];
      return prizes.find(item => item.id === prizeId) || null;
    }

    async function requestBackendSpin(submittedEmail) {
      if (!backendUrl) throw new Error('BACKEND_URL_MISSING');

      const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
      const timeoutId = window.setTimeout(() => {
        if (controller) controller.abort();
      }, 10000);

      try {
        const response = await fetch(`${backendUrl}/spin`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: submittedEmail,
            marketingConsent: Boolean(marketingConsent && marketingConsent.checked)
          }),
          signal: controller ? controller.signal : undefined
        });

        let payload = null;
        try { payload = await response.json(); } catch (e) {}

        if (!response.ok) {
          const error = new Error((payload && payload.error) || 'BACKEND_ERROR');
          error.code = payload && payload.code ? payload.code : `HTTP_${response.status}`;
          throw error;
        }

        const prize = prizeFromBackend(payload);
        if (!prize || !payload || !payload.code) throw new Error('INVALID_BACKEND_RESPONSE');

        return {
          prize,
          code: payload.code,
          expiresAt: payload.expiresAt ? Date.parse(payload.expiresAt) : null
        };
      } finally {
        window.clearTimeout(timeoutId);
      }
    }

    function generatePreviewCode(prize) {
      const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
      let suffix = '';
      for (let i = 0; i < 6; i += 1) suffix += chars[Math.floor(Math.random() * chars.length)];
      return `${prize.codePrefix}-${suffix}`;
    }

    function rotateTo(index) {
      const segmentCenter = -54 + (index * 72);
      const target = ((-90 - segmentCenter) % 360 + 360) % 360;
      const currentMod = ((rotation % 360) + 360) % 360;
      const delta = (target - currentMod + 360) % 360;
      rotation += (360 * 5) + delta;
      disc.classList.remove('is-spinning');
      void disc.offsetWidth;
      disc.classList.add('is-spinning');
      disc.style.transform = `rotate(${rotation}deg)`;
    }

    function clearConfetti() {
      if (!confettiLayer) return;
      confettiLayer.innerHTML = '';
      confettiLayer.hidden = true;
    }

    function buildConfetti(isJackpot) {
      if (!confettiLayer || !confettiEnabled) return;
      clearConfetti();
      confettiLayer.hidden = false;
      const count = isJackpot ? 42 : 30;
      const palette = isJackpot
        ? ['#D5A62E', '#B67A16', '#F1D779', '#8A5A18', '#F7E9BA']
        : ['#BDA88B', '#D8C7AF', '#E7DED0', '#9D8768', '#F2ECE2'];
      for (let i = 0; i < count; i += 1) {
        const piece = document.createElement('span');
        piece.className = 'vitea-wheel__confetti-piece';
        piece.style.setProperty('--x', `${4 + Math.random() * 92}%`);
        piece.style.setProperty('--delay', `${Math.random() * .28}s`);
        piece.style.setProperty('--duration', `${1.45 + Math.random() * .85}s`);
        piece.style.setProperty('--drift', `${-38 + Math.random() * 76}px`);
        piece.style.setProperty('--rot', `${180 + Math.random() * 540}deg`);
        piece.style.setProperty('--piece-color', palette[i % palette.length]);
        piece.style.setProperty('--piece-width', `${5 + Math.random() * 5}px`);
        piece.style.setProperty('--piece-height', `${8 + Math.random() * 7}px`);
        confettiLayer.appendChild(piece);
      }
    }

    function showCelebration(result) {
      if (!celebrationEnabled || !celebration) return Promise.resolve();
      if (entryPanel) entryPanel.hidden = true;
      if (celebrationKicker) celebrationKicker.textContent = text.celebration_kicker || 'Gefeliciteerd!';
      if (celebrationPrize) celebrationPrize.textContent = result.title;
      if (celebrationSub) celebrationSub.textContent = text.celebration_sub || 'Jouw Vitea-prijs staat klaar.';
      celebration.classList.toggle('is-jackpot', Boolean(result.jackpot));
      celebration.hidden = false;
      requestAnimationFrame(() => celebration.classList.add('is-visible'));
      buildConfetti(Boolean(result.jackpot));
      return new Promise(resolve => {
        window.setTimeout(() => {
          celebration.classList.remove('is-visible');
          window.setTimeout(() => {
            celebration.hidden = true;
            clearConfetti();
            if (entryPanel) entryPanel.hidden = false;
            resolve();
          }, 260);
        }, celebrationSeconds * 1000);
      });
    }

    function formatCountdown(ms) {
      if (ms <= 0) return '00:00:00';
      const total = Math.floor(ms / 1000);
      const hours = Math.floor(total / 3600);
      const minutes = Math.floor((total % 3600) / 60);
      const seconds = total % 60;
      return [hours, minutes, seconds].map(v => String(v).padStart(2, '0')).join(':');
    }

    function saveResult(result) {
      try { localStorage.setItem(resultKey, JSON.stringify(result)); } catch (e) {}
    }

    function loadResult() {
      if (designMode) return null;
      try {
        const raw = localStorage.getItem(resultKey);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || !parsed.prizeId || !parsed.code) return null;
        if (!parsed.jackpot && Number(parsed.expiresAt) <= Date.now()) {
          localStorage.removeItem(resultKey);
          return null;
        }
        return parsed;
      } catch (e) {
        return null;
      }
    }

    function showResult(result, minimized = false) {
      if (!result) return;
      hideTeaser();
      resultTitle.textContent = result.title;
      resultCode.textContent = result.code;
      miniTitle.textContent = result.title;
      resultCard.classList.toggle('is-jackpot', Boolean(result.jackpot));
      miniResult.classList.toggle('is-jackpot', Boolean(result.jackpot));

      const timerRow = timerValue ? timerValue.closest('[data-wheel-timer-row]') : null;
      if (result.jackpot) {
        if (timerRow) timerRow.hidden = true;
        miniTimer.textContent = text.jackpot_saved || 'Jackpot bewaard';
      } else if (timerRow) {
        timerRow.hidden = false;
      }

      resultCard.hidden = minimized;
      miniResult.hidden = !minimized;
      startTimer(result);
    }

    function startTimer(result) {
      if (timerId) clearInterval(timerId);
      const tick = () => {
        if (result.jackpot) return;
        const remaining = Number(result.expiresAt) - Date.now();
        const formatted = formatCountdown(remaining);
        timerValue.textContent = formatted;
        miniTimer.textContent = formatted;
        if (remaining <= 0) {
          clearInterval(timerId);
          resultCard.hidden = true;
          miniResult.hidden = true;
          try { localStorage.removeItem(resultKey); } catch (e) {}
          const storedDiscount = loadStoredWheelDiscount();
          if (storedDiscount && storedDiscount.code.toLowerCase() === String(result.code || '').toLowerCase()) {
            void removeExpiredWheelDiscount(storedDiscount);
          }
          showTeaser();
        }
      };
      tick();
      timerId = setInterval(tick, 1000);
    }

    async function copyCode() {
      const code = resultCode.textContent.trim();
      if (!code) return;
      let copied = false;
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(code);
          copied = true;
        }
      } catch (e) {}
      if (!copied) {
        const temp = document.createElement('textarea');
        temp.value = code;
        temp.style.position = 'fixed';
        temp.style.opacity = '0';
        document.body.appendChild(temp);
        temp.select();
        try { copied = document.execCommand('copy'); } catch (e) {}
        temp.remove();
      }
      const original = copyButton.textContent;
      copyButton.textContent = copied ? (text.copied || 'Gekopieerd') : original;
      setTimeout(() => { copyButton.textContent = original; }, 1800);
    }

    function resetEditorTest() {
      if (timerId) clearInterval(timerId);
      resultCard.hidden = true;
      miniResult.hidden = true;
      if (celebration) { celebration.hidden = true; celebration.classList.remove('is-visible', 'is-jackpot'); }
      if (entryPanel) entryPanel.hidden = false;
      clearConfetti();
      previewTeaserReopened = false;
      previewTeaserCollapsed = false;
      teaserWrap.classList.remove('is-collapsed');
      if (teaserDismiss) teaserDismiss.hidden = true;
      teaserWrap.hidden = false;
      spinButton.disabled = false;
      disc.classList.remove('is-spinning');
      disc.style.transform = 'rotate(0deg)';
      rotation = 0;
      if (email) email.value = '';
      errorEl.textContent = '';
    }

    teaser.addEventListener('click', event => {
      if (teaser.dataset.wheelDragged === 'true') {
        event.preventDefault();
        return;
      }
      markAutoPromptSeen();
      markTeaserReopened();
      setTeaserCollapsed(false);
      teaserWrap.classList.remove('is-collapsed');
      if (teaserDismiss) teaserDismiss.hidden = true;
      hideTeaser();
      openModal({ clearEmail: designMode });
    });

    if (teaserDismiss) {
      teaserDismiss.addEventListener('click', event => {
        event.preventDefault();
        event.stopPropagation();

        // Nooit volledig laten verdwijnen. Na de bewuste dismiss wordt
        // de teaser alleen een kleiner cadeau-tabje aan de schermrand.
        if (!teaserWasReopened()) return;
        setTeaserCollapsed(true);
        showTeaser();
      });
    }

    closeButtons.forEach(btn => btn.addEventListener('click', closeModalAndRevealTab));
    overlay.addEventListener('click', event => { if (event.target === overlay) closeModalAndRevealTab(); });
    document.addEventListener('keydown', event => { if (event.key === 'Escape' && overlay.classList.contains('is-open')) closeModalAndRevealTab(); });
    copyButton.addEventListener('click', copyCode);
    minimizeButton.addEventListener('click', () => {
      const existing = designMode ? null : loadResult();
      if (existing) showResult(existing, true);
      else {
        resultCard.hidden = true;
        miniResult.hidden = false;
      }
    });
    miniResult.addEventListener('click', event => {
      if (miniResult.dataset.wheelDragged === 'true') {
        event.preventDefault();
        return;
      }
      const existing = designMode ? null : loadResult();
      if (existing) showResult(existing, false);
      else {
        miniResult.hidden = true;
        resultCard.hidden = false;
      }
    });
    if (editorReset) editorReset.addEventListener('click', resetEditorTest);

    form.addEventListener('submit', async event => {
      event.preventDefault();
      errorEl.textContent = '';
      if (!email || !email.checkValidity()) {
        errorEl.textContent = text.email_error || 'Vul een geldig e-mailadres in.';
        if (email) email.focus();
        return;
      }

      const submittedEmail = normalizeEmail(email.value);
      const useSecureBackend = backendEnabled && !designMode;

      // In lokale/Theme Editor-testmodus gebruiken we de browsercontrole.
      // Zodra de beveiligde backend actief is, is de database de enige bron van waarheid.
      if (!useSecureBackend && isEmailUsed(submittedEmail)) {
        errorEl.textContent = text.email_used || 'Dit e-mailadres heeft al meegedaan. Gebruik een ander e-mailadres om te draaien.';
        email.focus();
        return;
      }

      spinButton.disabled = true;

      let prize;
      let resultCode;
      let resultExpiresAt;

      try {
        if (useSecureBackend) {
          const backendResult = await requestBackendSpin(submittedEmail);
          prize = backendResult.prize;
          resultCode = backendResult.code;
          resultExpiresAt = backendResult.expiresAt;
        } else {
          const forcedPrize = testMode && forcedPrizeId !== 'random' ? prizes.find(item => item.id === forcedPrizeId) : null;
          prize = forcedPrize || weightedPick();
          resultCode = generatePreviewCode(prize);
          resultExpiresAt = prize.jackpot ? null : Date.now() + (discountHours * 60 * 60 * 1000);
        }
      } catch (error) {
        spinButton.disabled = false;
        if (error && error.code === 'EMAIL_ALREADY_USED') {
          errorEl.textContent = text.email_used || 'Dit e-mailadres heeft al meegedaan. Gebruik een ander e-mailadres om te draaien.';
          email.focus();
          return;
        }
        if (error && error.code === 'INVALID_EMAIL') {
          errorEl.textContent = text.email_error || 'Vul een geldig e-mailadres in.';
          email.focus();
          return;
        }
        console.error('[Vitea Prize Wheel] Backend error:', error);
        errorEl.textContent = text.server_error || 'Het rad kan nu even niet draaien. Probeer het over een moment opnieuw.';
        return;
      }

      rotateTo(prize.index);
      const now = Date.now();
      const result = {
        prizeId: prize.id,
        title: prize.title,
        jackpot: prize.jackpot,
        code: resultCode,
        email: submittedEmail,
        wonAt: now,
        expiresAt: prize.jackpot ? null : (Number.isFinite(resultExpiresAt) ? resultExpiresAt : now + (discountHours * 60 * 60 * 1000))
      };

      if (!designMode) {
        if (!useSecureBackend) markEmailUsed(submittedEmail);
        saveResult(result);
        if (useSecureBackend && !result.jackpot) void applyWheelDiscount(result);
      }

      window.setTimeout(async () => {
        await showCelebration(result);
        closeModal({ skipRestore: true });
        showResult(result, false);
        spinButton.disabled = false;
      }, 5350);
    });

    applyTabPosition(loadTabPosition());
    makeVerticallyDraggable(teaser);
    makeVerticallyDraggable(miniResult);

    if (designMode) {
      // Theme Editor is a sandbox: previous spins never hide or lock the wheel.
      resetEditorTest();
      if (editorReset) editorReset.hidden = false;
    } else {
      const existing = loadResult();
      reconcileWheelDiscount(existing);
      if (existing) showResult(existing, true);
      else scheduleAutoOpen();
    }
  }

  function initAll(scope = document) {
    if (scope.matches && scope.matches(ROOT_SELECTOR)) init(scope);
    scope.querySelectorAll(ROOT_SELECTOR).forEach(init);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();

  document.addEventListener('shopify:section:load', event => initAll(event.target));
})();
